import React, { useRef, useState } from 'react';
import { CONTACT_EMAIL, type SchoolConfig } from '../../content/schools';
import {
  submitSchoolInquiry,
  trackOutreachEvent,
  type OutreachAttribution,
  type SchoolInquiryInput,
} from '../../utils/outreachApi';
import {
  AUDIENCE_SIZES,
  COLLABORATION_OPTIONS,
  EMAIL_PATTERN,
  INQUIRY_CONSENT_LABEL,
  SCHOOL_ROLES,
} from './formOptions';
import {
  ConsentField,
  HoneypotField,
  RadioGroupField,
  SelectField,
  SubmitError,
  TextAreaField,
  TextField,
  focusFirstError,
  type FormStatus,
} from './fields';

type Errors = Partial<Record<keyof SchoolInquiryInput, string>>;

function validate(v: SchoolInquiryInput): Errors {
  const errors: Errors = {};
  if (!v.name.trim()) errors.name = 'Name is required.';
  if (!v.workEmail.trim()) errors.workEmail = 'Work email is required.';
  else if (!EMAIL_PATTERN.test(v.workEmail.trim())) errors.workEmail = 'Enter a valid email address.';
  if (!v.organization.trim()) errors.organization = 'School or organization is required.';
  if (!v.role) errors.role = 'Choose your role.';
  if (!v.preferredCollaboration) errors.preferredCollaboration = 'Choose how you’d like to collaborate.';
  if (!v.consentToRespond) errors.consentToRespond = 'Please confirm we can reply to your inquiry.';
  return errors;
}

export function SchoolInquiryForm({
  school,
  attribution,
}: {
  school: SchoolConfig;
  attribution: OutreachAttribution;
}) {
  const [values, setValues] = useState<SchoolInquiryInput>({
    name: '',
    workEmail: '',
    organization: school.name,
    role: '',
    preferredCollaboration: '',
    website: '',
    audienceSize: '',
    details: '',
    consentToRespond: false,
    hfsCheck: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [failure, setFailure] = useState<string>('error');
  const started = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  function set<K extends keyof SchoolInquiryInput>(key: K, value: SchoolInquiryInput[K]) {
    if (!started.current) {
      started.current = true;
      trackOutreachEvent('school_inquiry_start', school.slug);
    }
    setValues(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;

    const clientErrors = validate(values);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFailure('validation');
      setStatus('error');
      focusFirstError(formRef.current, clientErrors as Record<string, string>);
      return;
    }

    setStatus('submitting');
    const result = await submitSchoolInquiry(values, attribution);
    if (result.ok) {
      setStatus('success');
      requestAnimationFrame(() => successRef.current?.focus());
      return;
    }
    if (result.fieldErrors) {
      setErrors(result.fieldErrors as Errors);
      focusFirstError(formRef.current, result.fieldErrors);
    }
    setFailure(result.reason);
    setStatus('error');
  }

  if (status === 'success') {
    return (
      <div ref={successRef} tabIndex={-1} className="sl-alert sl-alert-success" role="status">
        <h3 className="sl-alert-title">Thanks—we’ve received your inquiry.</h3>
        <p>We’ll reply to <strong>{values.workEmail.trim()}</strong>. There’s no commitment involved in starting the conversation.</p>
      </div>
    );
  }

  const submitting = status === 'submitting';

  return (
    <form ref={formRef} className="sl-form" onSubmit={handleSubmit} noValidate aria-busy={submitting}>
      <p className="sl-form-note">Fields marked <span className="sl-req">*</span> are required.</p>

      <div className="sl-form-row">
        <TextField
          id="name" label="Name" required autoComplete="name" maxLength={120}
          value={values.name} onChange={v => set('name', v)} error={errors.name}
        />
        <TextField
          id="workEmail" label="Work email" type="email" required autoComplete="email" inputMode="email" maxLength={254}
          value={values.workEmail} onChange={v => set('workEmail', v)} error={errors.workEmail}
        />
      </div>

      <div className="sl-form-row">
        <TextField
          id="organization" label="School or organization" required autoComplete="organization" maxLength={150}
          value={values.organization} onChange={v => set('organization', v)} error={errors.organization}
        />
        <SelectField
          id="role" label="Your role" required options={SCHOOL_ROLES}
          value={values.role} onChange={v => set('role', v)} error={errors.role}
        />
      </div>

      <RadioGroupField
        id="preferredCollaboration" label="How would you prefer to collaborate?" required
        options={COLLABORATION_OPTIONS}
        value={values.preferredCollaboration} onChange={v => set('preferredCollaboration', v)}
        error={errors.preferredCollaboration}
      />

      <div className="sl-form-row">
        <TextField
          id="website" label="School website" type="url" autoComplete="url" inputMode="url" maxLength={300}
          value={values.website} onChange={v => set('website', v)} error={errors.website}
        />
        <SelectField
          id="audienceSize" label="Approximate student audience" options={AUDIENCE_SIZES}
          value={values.audienceSize} onChange={v => set('audienceSize', v)} error={errors.audienceSize}
        />
      </div>

      <TextAreaField
        id="details" label="Anything else we should know?" maxLength={2000}
        value={values.details} onChange={v => set('details', v)} error={errors.details}
      />

      <ConsentField
        id="consentToRespond" label={INQUIRY_CONSENT_LABEL}
        checked={values.consentToRespond} onChange={v => set('consentToRespond', v)}
        error={errors.consentToRespond}
      />

      <HoneypotField id="inquiry_hfs_check" value={values.hfsCheck} onChange={v => setValues(prev => ({ ...prev, hfsCheck: v }))} />

      {status === 'error' && <SubmitError reason={failure} contactEmail={CONTACT_EMAIL} />}

      <button type="submit" className="sl-btn sl-btn-primary sl-btn-block" disabled={submitting}>
        {submitting ? 'Sending…' : 'Send inquiry'}
      </button>
      <p className="sl-fine">
        Please don’t send student contact details—students apply for themselves through this page.
        This site is protected by reCAPTCHA.
      </p>
    </form>
  );
}
